<template>
  <div>
    <v-sheet outlined rounded class="px-4 py-2">
      <v-form id="customer-general-settings-form" @submit.prevent="onSubmit">
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-text-field
              v-model="localCustomer.name"
              :label="$tc('name')"/>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-select
              v-model="selectedParentCustomer"
              :label="$tc('parent')"
              :items="parentOptions"
              item-value="id"
              item-text="name"
              return-object/>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-select
              v-model="selectedTimezone"
              :label="$tc('timezone')"
              :items="timeZoneOptions"
              item-value="value"
              item-text="text"
              return-object/>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-select
              v-model="temperatureFormat"
              :label="$tc('temperature_format')"
              :items="temperatureFormatOptions"
              item-value="value"
              item-text="text" />
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.temp_format_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-select
              v-model="distanceFormat"
              :label="$tc('distance_format')"
              :items="distanceFormatOptions"
              item-value="value"
              item-text="text" />
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.distance_format_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" lg="6" cols="12">
            <v-row class="align-center">
              <v-col cols="8">
                {{ $tc('route_plan_feed') }}
                <v-badge class="ml-2" color="primary" :content="$t('enterprise')" inline/>
              </v-col>
              <v-col>
                <v-checkbox :label="$t('enable')" v-model.number="routePlanFeedEnabled" :true-value="1" :false-value="0"/>
              </v-col>
            </v-row>
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.route_plan_feed_enabled_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-text-field
              v-model.number="routePlanMatchThreshold"
              :label="$tc('route_plan_match_threshold')"/>
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.route_plan_match_threshold') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-textarea
              v-model="routeNames"
              :label="$tc('master_route_list')"
              rows="4"
              auto-grow/>
            <v-checkbox v-model.number="routePlanRouteCreate" :true-value="1" :false-value="0">
              <template v-slot:label>
                {{ $t('customer_general_settings_ui.use_route_plan_feed') }}
                <vf-tooltip :text="$t('customer_general_settings_ui.use_route_plan_feed_help')">
                  <v-icon>mdi-help-circle</v-icon>
                </vf-tooltip>
                <v-badge class="ml-2" color="primary" :content="$t('enterprise')" inline/>
              </template>
            </v-checkbox>
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.master_route_list_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-select
              v-model="routePlanFormat"
              :label="$tc('route_plan_format')"
              :items="routePlanFormatOptions"
              item-value="value"
              item-text="text" />
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.route_plan_format_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" sm="6" md="6" lg="6" xl="6">
            <v-row class="align-center">
              <v-col cols="8">
                {{ $t('root_cause_analysis') }}
                <v-badge class="ml-2" color="primary" :content="$t('enterprise')" inline/>
              </v-col>
              <v-col>
                <v-checkbox :label="$t('enable')" v-model.number="enableViolationRootCauses" :true-value="1" :false-value="0" />
              </v-col>
            </v-row>
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.root_cause_analysis_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" sm="6" md="6" lg="6" xl="6">
            <v-row class="align-center">
              <v-col cols="8">
                {{ $tc('require_manual_temps_in_root_cause_assessment') }}
                <v-badge class="ml-2" color="primary" :content="$t('enterprise')" inline/>
              </v-col>
              <v-col>
                <v-checkbox :label="$t('enable')" v-model.number="requireManualTempsInRootCauseAssessment" :true-value="1" :false-value="0"/>
              </v-col>
            </v-row>
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.require_manual_temps_in_root_cause_assessment_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" sm="6" md="6" lg="6" xl="6">
            <v-row class="align-center">
              <v-col cols="8">
                {{ $tc('daily_start_temp') }}
              </v-col>
              <v-col>
                <v-checkbox :label="$t('enable')" v-model.number="enableDailyStartTemp" :true-value="1" :false-value="0" />
                <v-text-field v-if="enableDailyStartTemp" v-model="dailyStartTempExpiration"></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.daily_start_temp_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-text-field
              v-model="supportEmail"
              :label="$tc('support_email')"/>
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.support_email_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-text-field
              v-model.number="dataGapFatalDuration"
              :label="$tc('data_gap_fatal_duration')"/>
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.data_gap_fatal_duration_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-text-field
              v-model.number="dataGapWarningDuration"
              :label="$tc('data_gap_warning_duration')"/>
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.data_gap_warning_duration_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-text-field
              v-model.number="routeAssessmentTimeLimit"
              :label="$tc('customer_general_settings_ui.route_assessment_time_limit')"/>
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.route_assessment_time_limit_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-text-field
              v-model.number="maximumRouteDurationHours"
              :label="$tc('customer_general_settings_ui.maximum_route_duration_hours')"/>
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.maximum_route_duration_hours_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" sm="6" md="6" lg="6" xl="6">
            <v-row class="align-center">
              <v-col cols="8">
                {{ $tc('simulated_temperature_forecast') }}
                <v-badge class="ml-2" color="primary" :content="$t('enterprise')" inline/>
              </v-col>
              <v-col>
                <v-checkbox :label="$t('enable')" v-model.number="enableSimulatedTemperatureForecast" :true-value="1" :false-value="0"/>
              </v-col>
            </v-row>
            <!-- Full Route forecasting sub-setting (only show when base forecasting is enabled) -->
            <v-row v-if="enableSimulatedTemperatureForecast === 1" class="align-center ml-4">
              <v-col cols="8">
                {{ $t('customer_general_settings_ui.full_route_forecasting') }}
                <v-badge class="ml-2" color="orange" :content="$t('premium')" inline/>
              </v-col>
              <v-col>
                <v-checkbox :label="$t('enable')" v-model.number="enableSimulatedTemperatureForecastFullRoute" :true-value="1" :false-value="0"/>
              </v-col>
            </v-row>
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.simulated_temperature_forecast_help') }}
            <div v-if="enableSimulatedTemperatureForecast === 1" class="mt-2">
              <strong>{{ $t('customer_general_settings_ui.full_route_forecasting') }}:</strong>
              {{ $t('customer_general_settings_ui.full_route_forecasting_help') }}
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-text-field
              v-model.number="routesInSpecTarget"
              :label="$tc('reports_ui.weekly_performance_report.routes_in_spec_target')"
              type="number"
              suffix="%"
              min="0"
              max="100" />
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.routes_in_spec_target_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-text-field
              v-model.number="linkedPlansTarget"
              :label="$tc('reports_ui.weekly_performance_report.linked_plans_target')"
              type="number"
              suffix="%"
              min="0"
              max="100" />
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.linked_plans_target_help') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-text-field
              v-model.number="rootCausesTarget"
              :label="$tc('reports_ui.weekly_performance_report.completed_root_causes_target')"
              type="number"
              suffix="%"
              min="0"
              max="100" />
          </v-col>
          <v-col>
            {{ $t('customer_general_settings_ui.root_causes_target_help') }}
          </v-col>
        </v-row>
        <v-divider class="my-4"></v-divider>
        <v-row>
          <v-col cols="12">
            <h3 class="text-h6 mb-3">Billing Settings</h3>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-text-field
              v-model="billingCostPerVehicle"
              :label="'Cost Per Vehicle'"
              type="text"
              placeholder="50.00 or 0:50,10:45,100:40"
              :error-messages="$v.billingCostPerVehicle.$invalid && $v.billingCostPerVehicle.$dirty ? ['Invalid pricing format'] : []"/>
          </v-col>
          <v-col>
            <strong>Simple pricing:</strong> Enter a fixed price per vehicle (e.g., 50.00).<br>
            <strong>Tiered pricing:</strong> Use format start:price,start:price (e.g., 0:50,10:45,100:40).<br>
            The example charges $50 for vehicles 0-9, $45 for 10-99, and $40 for 100+.
          </v-col>
        </v-row>
        <v-row>
          <v-col class="ml-2" md="6" cols="12">
            <v-text-field
              v-model.number="billingMinimumAmount"
              :label="'Minimum Billing Amount'"
              type="number"
              step="0.01"
              min="0"
              prefix="$"
              placeholder="0.00"
              :error-messages="$v.billingMinimumAmount.$invalid && $v.billingMinimumAmount.$dirty ? ['Must be 0 or greater'] : []"/>
          </v-col>
          <v-col>
            Minimum monthly billing amount. If calculated billing is below this threshold, a warning will be displayed in the invoice report.
          </v-col>
        </v-row>
        <v-btn type="submit"
               color="primary"
               @click="$gtag.event('update', {event_category: 'Customer'})"
               :loading="isSaving"
               block >
          <v-icon class="ml-n2">mdi-content-save</v-icon>
          {{ $t('update') }}
        </v-btn>
      </v-form>
    </v-sheet>
  </div>
</template>

<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import VfTooltip from '@/components/shared/VfTooltip'
import Moment from 'moment-timezone'
import { between, minValue } from 'vuelidate/lib/validators'
import { map, sortBy, find, cloneDeep, filter } from 'lodash'

export default {
  name: 'CustomerGeneralSettings',
  components: {
    VfTooltip
  },
  mixins: [CurrentUserMixin, ApiUtilitiesMixin],
  props: {
    customer: Object
  },
  data () {
    return {
      isSaving: false,
      timeZoneOptions: this.getTimeZoneOptions(),
      localCustomer: cloneDeep(this.customer),
      // Form values
      timeZone: this.customer.getSetting('tz'),
      temperatureFormat: this.customer.getSetting('tempFormat'),
      distanceFormat: this.customer.getSetting('distanceFormat'),
      routeNames: this.customer.getSetting('routeNames'),
      routePlanMatchThreshold: this.customer.getSetting('routePlanMatchThreshold'),
      routePlanRouteCreate: this.customer.getSetting('routePlanRouteCreate'),
      routePlanFeedEnabled: this.customer.getSetting('routePlanFeedEnabled'),
      routePlanFormat: this.customer.getSetting('routePlanFormat'),
      enableViolationRootCauses: this.customer.getSetting('enableViolationRootCauses'),
      enableDailyStartTemp: this.customer.getSetting('enableDailyStartTemp'),
      dailyStartTempExpiration: this.customer.getSetting('dailyStartTempExpiration'),
      supportEmail: this.customer.getSetting('identity_provider_support_email'),
      dataGapFatalDuration: this.customer.getSetting('data_gap_fatal_duration'),
      dataGapWarningDuration: this.customer.getSetting('data_gap_warning_duration'),
      routeAssessmentTimeLimit: this.customer.getSetting('performanceDashboard.routeAssessments.timeLimit'),
      maximumRouteDurationHours: this.customer.getSetting('maximum_route_duration_hours'),
      enableSimulatedTemperatureForecast: this.customer.getSetting('enableSimulatedTemperatureForecast'),
      enableSimulatedTemperatureForecastFullRoute: this.customer.getSetting('enableSimulatedTemperatureForecastFullRoute'),
      requireManualTempsInRootCauseAssessment: this.customer.getSetting('requireManualTempsInRootCauseAssessment'),
      routesInSpecTarget: this.customer.getSetting('routesInSpecTarget', null),
      linkedPlansTarget: this.customer.getSetting('linkedPlansTarget', null),
      rootCausesTarget: this.customer.getSetting('rootCausesTarget', null),
      billingCostPerVehicle: this.customer.getSetting('billingCostPerVehicle', '0'),
      billingMinimumAmount: this.customer.getSetting('billingMinimumAmount', 0)
    }
  },
  validations: {
    maximumRouteDurationHours: {
      between: between(1, 120)
    },
    routesInSpecTarget: { between: between(0, 100) },
    linkedPlansTarget: { between: between(0, 100) },
    rootCausesTarget: { between: between(0, 100) },
    billingCostPerVehicle: {
      validPricing (value) {
        return this.validatePricingFormat(value)
      }
    },
    billingMinimumAmount: { minValue: minValue(0) }
  },
  methods: {
    /**
     * Validate pricing format - supports both simple decimal and tiered pricing
     */
    validatePricingFormat (value) {
      if (!value || value === '') {
        return true // Allow empty values
      }

      // Check if it's a simple numeric value
      const numericValue = parseFloat(value)
      if (!isNaN(numericValue) && numericValue >= 0 && /^\d+(\.\d+)?$/.test(value.toString().trim())) {
        return true
      }

      // Check if it's tiered pricing format (e.g., "0:50,10:45,100:40")
      const tieredPattern = /^\d+:\d+(\.\d+)?(,\d+:\d+(\.\d+)?)*$/
      if (!tieredPattern.test(value)) {
        return false
      }

      // Validate tiered structure
      const tiers = value.split(',').map(tier => {
        const [start, price] = tier.split(':')
        return {
          start: parseInt(start),
          price: parseFloat(price)
        }
      })

      // Check for valid numbers and unique starts
      const starts = new Set()
      for (const tier of tiers) {
        if (isNaN(tier.start) || isNaN(tier.price) || tier.start < 0 || tier.price < 0) {
          return false
        }
        if (starts.has(tier.start)) {
          return false // Duplicate start values
        }
        starts.add(tier.start)
      }

      return true
    },
    /**
     * Get all of the time zone options from Moment and put them into objects for the selector to use.
     */
    getTimeZoneOptions () {
      const timeZoneOptions = map(Moment.tz.names(), (name) => {
        const zone = Moment.tz(name)
        return {
          value: name,
          offset: zone.utcOffset(),
          text: `(UTC${zone.format('Z')}) ${name}`
        }
      })
      return sortBy(timeZoneOptions, ['offset', 'value'])
    },
    /**
     * Submit the form. Only attempt to save the settings that are set.
     */
    onSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return false
      }

      this.isSaving = true

      const updatedSettings = {}
      if (this.timeZone != null) {
        updatedSettings.tz = this.timeZone
      }
      if (this.temperatureFormat != null) {
        updatedSettings.tempFormat = this.temperatureFormat
      }
      if (this.distanceFormat != null) {
        updatedSettings.distanceFormat = this.distanceFormat
      }

      updatedSettings.routeNames = this.routeNames !== '' ? this.routeNames : null

      updatedSettings.routePlanMatchThreshold = this.routePlanMatchThreshold !== '' ? this.routePlanMatchThreshold : null

      if (this.routePlanFeedEnabled != null) {
        updatedSettings.routePlanFeedEnabled = this.routePlanFeedEnabled
      }
      if (this.routePlanRouteCreate != null) {
        updatedSettings.routePlanRouteCreate = this.routePlanRouteCreate
      }
      if (this.routePlanFormat != null) {
        updatedSettings.routePlanFormat = this.routePlanFormat
      }
      if (this.routePlanFormat != null) {
        updatedSettings.routePlanFormat = this.routePlanFormat
      }
      if (this.enableViolationRootCauses != null) {
        updatedSettings.enableViolationRootCauses = this.enableViolationRootCauses
      }
      if (this.enableDailyStartTemp != null) {
        updatedSettings.enableDailyStartTemp = this.enableDailyStartTemp
      }

      updatedSettings.dailyStartTempExpiration = this.dailyStartTempExpiration !== '' ? this.dailyStartTempExpiration : null

      updatedSettings.identity_provider_support_email = this.supportEmail !== '' ? this.supportEmail : null

      updatedSettings.data_gap_fatal_duration = this.dataGapFatalDuration !== '' ? this.dataGapFatalDuration : null
      updatedSettings.data_gap_warning_duration = this.dataGapWarningDuration !== '' ? this.dataGapWarningDuration : null

      updatedSettings.maximum_route_duration_hours = this.maximumRouteDurationHours !== '' ? this.maximumRouteDurationHours : null

      if (this.enableSimulatedTemperatureForecast != null) {
        updatedSettings.enableSimulatedTemperatureForecast = this.enableSimulatedTemperatureForecast
      }

      if (this.enableSimulatedTemperatureForecastFullRoute != null) {
        updatedSettings.enableSimulatedTemperatureForecastFullRoute = this.enableSimulatedTemperatureForecastFullRoute
      }

      if (this.requireManualTempsInRootCauseAssessment != null) {
        updatedSettings.requireManualTempsInRootCauseAssessment = this.requireManualTempsInRootCauseAssessment
      }
      if (this.routeAssessmentTimeLimit != null) {
        updatedSettings['performanceDashboard.routeAssessments.timeLimit'] = this.routeAssessmentTimeLimit
      }

      if (this.routesInSpecTarget !== null) {
        updatedSettings.routesInSpecTarget = this.routesInSpecTarget
      }
      if (this.linkedPlansTarget !== null) {
        updatedSettings.linkedPlansTarget = this.linkedPlansTarget
      }
      if (this.rootCausesTarget !== null) {
        updatedSettings.rootCausesTarget = this.rootCausesTarget
      }

      // Billing settings
      if (this.billingCostPerVehicle !== null && this.billingCostPerVehicle !== '') {
        // Check if it's a simple numeric value or tiered pricing
        const numericValue = parseFloat(this.billingCostPerVehicle)
        if (!isNaN(numericValue) && /^\d+(\.\d+)?$/.test(this.billingCostPerVehicle.toString().trim())) {
          // Simple numeric pricing
          updatedSettings.billingCostPerVehicle = numericValue
        } else {
          // Tiered pricing - store as string
          updatedSettings.billingCostPerVehicle = this.billingCostPerVehicle.toString().trim()
        }
      }
      if (this.billingMinimumAmount !== null && this.billingMinimumAmount !== '') {
        updatedSettings.billingMinimumAmount = parseFloat(this.billingMinimumAmount)
      }

      const customerPromise = this.$orm('Customer').createOrUpdate(this.localCustomer)
      const settingsPromise = this.customer.updateSettings(updatedSettings)

      Promise.all([customerPromise, settingsPromise])
        .then((promises) => {
          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$i18n.tc('customer')} ${this.$i18n.t('generic_save_success')}`
          )
        })
        .catch((error) => {
          if (error) {
            this.renderErrorModal(error)
            throw (error)
          }
        })
        .finally(() => {
          this.isSaving = false
        })
    }
  },
  computed: {
    /**
     * Computed value to act as a proxy for time zone selection
     */
    selectedTimezone: {
      get: function () {
        const zone = Moment.tz(this.timeZone)
        return {
          value: this.timeZone,
          offset: zone.utcOffset(),
          text: `(UTC${zone.format('Z')}) ${this.timeZone}`
        }
      },
      set: function (value) {
        this.timeZone = value.value
      }
    },
    /**
     * Computed value to act as a proxy for parent id selection
     */
    selectedParentCustomer: {
      get: function () {
        return find(this.user.customers, c => c.id === this.localCustomer.parent_id)
      },
      set: function (customer) {
        if (customer == null) {
          this.localCustomer.parent_id = null
        } else {
          this.localCustomer.parent_id = customer.id
        }
      }
    },
    /**
     * Filtered customer options for parent to exclude the current customer itself
     */
    parentOptions () {
      return filter(this.customers, c => c.id !== this.customer.id)
    },
    /**
     * Options for temperature format
     */
    temperatureFormatOptions () {
      return [
        { text: this.$t('celsius'), value: 'C' },
        { text: this.$t('fahrenheit'), value: 'F' }
      ]
    },
    /**
     * Options for distance format
     */
    distanceFormatOptions () {
      return [
        { text: this.$t('metric'), value: 'metric' },
        { text: this.$t('imperial'), value: 'imperial' }
      ]
    },
    /**
     * Options for route plan formats
     */
    routePlanFormatOptions () {
      return [
        { text: 'Paragon', value: 'Paragon' },
        { text: 'MBCsv', value: 'MBCsv' },
        { text: 'JDEdwards', value: 'JDEdwards' }
      ]
    }
  }
}
</script>

<style scoped lang="scss">
#customer-general-settings-form {
  .row {
    padding-top: 0rem;
    padding-bottom: 0rem;
    margin-bottom: 0;
  }

  .row:nth-child(even) {
    background-color: #F7F7F7
  }
}

.form-text {
  color: #737373;
}
</style>
